import React, { useState, useRef } from 'react';
import { X, AlertCircle, CheckCircle, Plus, Trash2, Pencil, Upload, Camera } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useTranslation } from '../hooks/useTranslation';
import { useAuthStore } from '../store/useAuthStore';

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  profile: {
    id: string;
    first_name: string;
    last_name: string;
    username: string;
    phone: string | null;
    secondary_phone: string | null;
    date_of_birth: string | null;
    avatar_url: string | null;
    email: string | null;
  } | null;
  onProfileUpdated: () => void;
}

export function ProfileModal({ isOpen, onClose, profile, onProfileUpdated }: ProfileModalProps) {
  const { user } = useAuthStore();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  // Field states
  const [editingField, setEditingField] = useState<string | null>(null);
  const [phone, setPhone] = useState(profile?.phone || '');
  const [secondaryPhone, setSecondaryPhone] = useState(profile?.secondary_phone || '');
  const [showSecondaryPhone, setShowSecondaryPhone] = useState(!!profile?.secondary_phone);
  const [dateOfBirth, setDateOfBirth] = useState(profile?.date_of_birth || '');
  const [firstName, setFirstName] = useState(profile?.first_name || '');
  const [lastName, setLastName] = useState(profile?.last_name || '');
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUpdate = async (field: string, value: any) => {
    if (!profile?.id) return;

    setLoading(true);
    setError(null);

    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ [field]: value })
        .eq('id', profile.id);

      if (updateError) throw updateError;

      onProfileUpdated();
      setEditingField(null);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err instanceof Error ? err.message : t.common.error);
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneUpdate = async () => {
    if (!profile?.id) return;

    setLoading(true);
    setError(null);

    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ 
          phone,
          secondary_phone: showSecondaryPhone ? secondaryPhone : null
        })
        .eq('id', profile.id);

      if (updateError) throw updateError;

      onProfileUpdated();
      setEditingField(null);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err instanceof Error ? err.message : t.common.error);
    } finally {
      setLoading(false);
    }
  };

  const handleNameUpdate = async () => {
    if (!profile?.id) return;

    setLoading(true);
    setError(null);

    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ 
          first_name: firstName,
          last_name: lastName
        })
        .eq('id', profile.id);

      if (updateError) throw updateError;

      onProfileUpdated();
      setEditingField(null);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err instanceof Error ? err.message : t.common.error);
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !profile?.id) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      setError(t.dashboard.vehicles.addNew.errors.invalidFileType);
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB
      setError(t.dashboard.vehicles.addNew.errors.fileTooLarge);
      return;
    }

    setLoading(true);
    setError(null);
    setUploadProgress(0);

    try {
      // Upload file
      const fileExt = file.name.split('.').pop();
      const filePath = `${profile.id}/avatar.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, {
          upsert: true,
          onUploadProgress: (progress) => {
            const percentage = (progress.loaded / progress.total) * 100;
            setUploadProgress(percentage);
          },
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      // Update profile
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', profile.id);

      if (updateError) throw updateError;

      onProfileUpdated();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch (err) {
      console.error('Error uploading avatar:', err);
      setError(err instanceof Error ? err.message : t.common.error);
    } finally {
      setLoading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (!isOpen) return null;

  if (success) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {t.common.success}
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            {t.profile.updateSuccess}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t.profile.title}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded flex items-start">
            <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <div className="space-y-4 mb-6">
          {/* Avatar */}
          <div className="flex justify-center">
            <div className="relative">
              <div className="w-24 h-24 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                {profile?.avatar_url ? (
                  <img
                    src={profile.avatar_url}
                    alt={t.profile.avatar}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <Camera className="w-8 h-8" />
                  </div>
                )}
              </div>
              <label
                htmlFor="avatar-upload"
                className="absolute bottom-0 right-0 bg-blue-600 text-white p-2 rounded-full cursor-pointer hover:bg-blue-700"
              >
                <Upload className="w-4 h-4" />
                <input
                  id="avatar-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleAvatarUpload}
                  ref={fileInputRef}
                />
              </label>
              {uploadProgress > 0 && (
                <div className="absolute -bottom-6 left-0 right-0">
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div
                      className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Name */}
          <div>
            <div className="flex justify-between items-center">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t.profile.name}
              </label>
              <button
                onClick={() => setEditingField(editingField === 'name' ? null : 'name')}
                className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400"
              >
                <Pencil className="w-4 h-4" />
              </button>
            </div>
            {editingField === 'name' ? (
              <div className="mt-1 space-y-2">
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder={t.auth.register.firstName}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white mb-2"
                />
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder={t.auth.register.lastName}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => {
                      setFirstName(profile?.first_name || '');
                      setLastName(profile?.last_name || '');
                      setEditingField(null);
                    }}
                    className="px-2 py-1 text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400"
                  >
                    {t.common.cancel}
                  </button>
                  <button
                    type="button"
                    onClick={handleNameUpdate}
                    className="px-2 py-1 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
                  >
                    {t.common.save}
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-gray-600 dark:text-gray-400">
                {profile?.first_name} {profile?.last_name}
              </p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t.auth.register.email}
            </label>
            <p className="text-gray-600 dark:text-gray-400">
              {profile?.email || user?.email}
            </p>
          </div>

          {/* Username */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t.auth.register.username}
            </label>
            <p className="text-gray-600 dark:text-gray-400">
              @{profile?.username}
            </p>
          </div>

          {/* Date of Birth */}
          <div>
            <div className="flex justify-between items-center">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t.profile.dateOfBirth}
              </label>
              <button
                onClick={() => setEditingField(editingField === 'dob' ? null : 'dob')}
                className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400"
              >
                <Pencil className="w-4 h-4" />
              </button>
            </div>
            {editingField === 'dob' ? (
              <div className="mt-1 space-y-2">
                <input
                  type="date"
                  value={dateOfBirth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => {
                      setDateOfBirth(profile?.date_of_birth || '');
                      setEditingField(null);
                    }}
                    className="px-2 py-1 text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400"
                  >
                    {t.common.cancel}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleUpdate('date_of_birth', dateOfBirth || null)}
                    className="px-2 py-1 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
                  >
                    {t.common.save}
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                {profile?.date_of_birth ? new Date(profile.date_of_birth).toLocaleDateString() : t.profile.notSet}
              </p>
            )}
          </div>

          {/* Phone Numbers */}
          <div>
            <div className="flex justify-between items-center">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t.profile.phoneNumbers}
              </label>
              <button
                onClick={() => setEditingField(editingField === 'phone' ? null : 'phone')}
                className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400"
              >
                <Pencil className="w-4 h-4" />
              </button>
            </div>
            {editingField === 'phone' ? (
              <div className="mt-1 space-y-3">
                <div>
                  <label className="text-sm text-gray-600 dark:text-gray-400">{t.profile.primary}</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+971 50 123 4567"
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>

                {showSecondaryPhone ? (
                  <div className="relative">
                    <label className="text-sm text-gray-600 dark:text-gray-400">{t.profile.secondary}</label>
                    <div className="flex gap-2 mt-1">
                      <input
                        type="tel"
                        value={secondaryPhone}
                        onChange={(e) => setSecondaryPhone(e.target.value)}
                        placeholder="+971 50 123 4567"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setShowSecondaryPhone(false);
                          setSecondaryPhone('');
                        }}
                        className="p-2 text-red-600 hover:text-red-700 dark:text-red-400"
                        title={t.profile.removeSecondaryPhone}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowSecondaryPhone(true)}
                    className="flex items-center text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    {t.profile.addSecondaryPhone}
                  </button>
                )}

                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => {
                      setPhone(profile?.phone || '');
                      setSecondaryPhone(profile?.secondary_phone || '');
                      setShowSecondaryPhone(!!profile?.secondary_phone);
                      setEditingField(null);
                    }}
                    className="px-2 py-1 text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400"
                  >
                    {t.common.cancel}
                  </button>
                  <button
                    type="button"
                    onClick={handlePhoneUpdate}
                    className="px-2 py-1 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
                  >
                    {t.common.save}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-1 mt-1">
                <p className="text-gray-600 dark:text-gray-400">
                  {t.profile.primary}: {profile?.phone || t.profile.notSet}
                </p>
                {profile?.secondary_phone && (
                  <p className="text-gray-600 dark:text-gray-400">
                    {t.profile.secondary}: {profile.secondary_phone}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}